import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
// store
import { useSelector, useDispatch, SET_RANGES_SENSOR } from 'store';
// components
import { Label, TextFields, Message as FormMessage } from '../../../components/hook-form';
// @devextreme
import ArrayStore from 'devextreme/data/array_store';
import Chart, { CommonSeriesSettings, Series, Legend, Tooltip, ConstantLine, ValueAxis, Annotation, CommonAxisSettings, VisualRange } from 'devextreme-react/chart';
import { Popover } from 'devextreme-react/popover';
// @moment
import moment from 'moment-timezone';
// utils
import { timezones } from '../../../utils/format';
// assets
import { ReactComponent as Line } from '../../../assets/real-time/line.svg';
import { ReactComponent as Recently } from '../../../assets/real-time/recently.svg';
import { ReactComponent as Settings } from 'assets/real-time/settings.svg';
// styles
import { LinePopover, RealTimeContent, RealTimeChart, RangesPopover, TextSwitch, PrimaryButton } from '../../../styles';
// apis
import { getRecently, getAiRecently, getTemperature } from '../../../apis';

// ----------------------------------------------------------------------

interface Props {
  source: any;
  axisRanges: any;
  info: any;
  aiRanges: any;
  aiLines: any;
};

// ----------------------------------------------------------------------

export default function SensorsChart({
  source, axisRanges, info, aiRanges, aiLines
}: Props) {
  const customizePoint = useCallback(({data}: any) => {
    if ( data.alarm && data.alarm !== 'NORMAL' ) return {color: '#FF6914', hoverStyle: {color: '#FF6914'}};
    return null;
  }, []);

  const customizeTooltip = ({point}: any) => {
    const value = point.data;
    return (
      <div>
        <p style={{'fontSize': '12px'}}>{ value.location }</p>
        <p style={{'fontSize': '13px', 'fontWeight': '600'}}>{ value.measuredValue + ' ' + info.current[value.id]?.unit }</p>
        <p style={{'fontSize': '11px', 'color': '#C3CAD9'}}>{ timezones(value.acquisitionAt, 'YYYY-MM-DD HH:mm:ss') }</p>
      </div>
    );
  };

  return (
    <Chart dataSource={source} size={{height: 274}} customizePoint={customizePoint}>
      <CommonAxisSettings tick={{visible: false}} />
      <CommonSeriesSettings type='bar' argumentField='location' hoverStyle={{color: '#3361FF', hatching: 'none'}} barWidth={20} minBarSize={10}  color='#A0BCE4' />
      <Series name='measuredValue' valueField='measuredValue'/>

      <ValueAxis
        showZero={true}
        maxValueMargin={0.15}
        minorTickInterval={0.001}
        valueMarginsEnabled={ axisRanges[source._key].manual ? false : true }
        title={{
          text: info.current[Object.keys(info.current).find((el:any) => info.current[el].name === source._key)]?.unit,
          alignment: 'center',
          font: {size: '12px', family: 'Pretendard, sans-serif'}
        }}
      >
        <VisualRange
          startValue={
            axisRanges[source._key].manual ?
              axisRanges[source._key].startValue
            : aiRanges[source._key]?.start ?
            Math.min(Math.min(...source._array.map((el: any) => el.measuredValue)), aiRanges[source._key]?.start || 0, 0 )
            : Math.min(Math.min(...source._array.map((el: any) => el.measuredValue)), 0 )
          }
          endValue={
            axisRanges[source._key].manual ?
              axisRanges[source._key].endValue
            : aiRanges[source._key]?.end ?
            Math.max(Math.max(...source._array.map((el: any) => el.measuredValue)), aiRanges[source._key]?.end || Math.max(...source._array.map((el: any) => el.measuredValue)) )
            : Math.max( ...source._array.map((el: any) => el.measuredValue) )
          }
        />
        { source._array.map((el: any) => (
          info.current[el.id]?.alarmLowerLimit !== null && aiLines[source._key] === el.id &&
          <ConstantLine key={el.id} value={info.current[el.id]?.alarmLowerLimit} label={{text: `${el.location} 하한`, font: {color: '#3361FF'}}} color='#3361FF' dashStyle='dash' />
        ))}
        { source._array.map((el: any) => (
          info.current[el.id]?.alarmUpperLimit !== null && aiLines[source._key] === el.id &&
            <ConstantLine key={el.id} value={info.current[el.id]?.alarmUpperLimit} label={{text: `${el.location} 상한`, font: {color: '#E62E2E'}}} color='#E62E2E' dashStyle='dash' />
        ))}
      </ValueAxis>

      { source._array.map((data: any, index: number) => (
        data.alarm && data.alarm !== 'NORMAL' &&
        <Annotation
          type='image'
          key={index}
          image={{url: '/assets/real-time/alram.png', width: 24, height: 24}}
          argument={data.location}
          data={data}
          color='transparent'
          border={{visible: false}}
          opacity={0}
          series='measuredValue'
          offsetY={-18}
        />
      ))}

      <Tooltip
        enabled={true}
        contentRender={customizeTooltip}
        border={{visible: false}}
        color='#4D5E80F2'
        arrowLength={0}
        cornerRadius={8}
        shadow={{ offsetX: 0, offsetY: 0, blur: 5, color: '#26334D', opacity: 0.2, family: 'Pretendard, sans-serif' }}
        font={{color: '#FFFFFF'}}
      />
      <Legend visible={false} />
    </Chart>
  );
}